import { toast } from "react-toastify";

import { createReducer } from "../../../common/reduxUtil";
import * as fulfillmentApis from "../../../common/apis/fulfillment.apis";

import { columnOf, nextChunk } from "./primitives";

/* Human-readable message off an API error. Mutation thunks surface failures
   via react-toastify (the app-wide toast container in App.jsx) so a failed
   assign/hold/complete is never swallowed silently. */
function errMsg(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

// ── Action Types ──

const FULFILLMENT_SET_COUNTS = "FULFILLMENT_SET_COUNTS";
const FULFILLMENT_SET_BOARD = "FULFILLMENT_SET_BOARD";
const FULFILLMENT_PATCH_ITEM = "FULFILLMENT_PATCH_ITEM";
const FULFILLMENT_REMOVE_ITEM = "FULFILLMENT_REMOVE_ITEM";
const FULFILLMENT_SET_COLUMN_DATA = "FULFILLMENT_SET_COLUMN_DATA";
const FULFILLMENT_SET_USERS = "FULFILLMENT_SET_USERS";
const FULFILLMENT_SET_BOARD_USERS = "FULFILLMENT_SET_BOARD_USERS";
const FULFILLMENT_SET_MY_TASKS = "FULFILLMENT_SET_MY_TASKS";
const FULFILLMENT_SET_PROVIDERS = "FULFILLMENT_SET_PROVIDERS";
const FULFILLMENT_SET_MANAGERS = "FULFILLMENT_SET_MANAGERS";
const FULFILLMENT_SET_PENDING_REQUESTS = "FULFILLMENT_SET_PENDING_REQUESTS";
const FULFILLMENT_SET_TEAM_ERROR = "FULFILLMENT_SET_TEAM_ERROR";
const FULFILLMENT_SET_LOADING = "FULFILLMENT_SET_LOADING";
const FULFILLMENT_SET_ERROR = "FULFILLMENT_SET_ERROR";
const FULFILLMENT_SET_ACTIVE_TAB = "FULFILLMENT_SET_ACTIVE_TAB";
const FULFILLMENT_RESET_COLUMNS = "FULFILLMENT_RESET_COLUMNS";
const FULFILLMENT_SET_ASSIGNMENT_POLICIES = "FULFILLMENT_SET_ASSIGNMENT_POLICIES";
const FULFILLMENT_SET_ASSIGNMENT_POLICY_DETAIL = "FULFILLMENT_SET_ASSIGNMENT_POLICY_DETAIL";

// ── Action Creators ──

function setLoading(key, value) {
  return { type: FULFILLMENT_SET_LOADING, payload: { key, value } };
}

function setError(error) {
  return { type: FULFILLMENT_SET_ERROR, payload: error };
}

function setActiveTab(tab) {
  return { type: FULFILLMENT_SET_ACTIVE_TAB, payload: tab };
}

// ── Page sizes ──
// Tasks per board column, and USERS per row of the two user-paginated lenses
// (roster rows, and the sections inside a user-sectioned column).
const COLUMN_PAGE_SIZE = 15;
const USER_PAGE_SIZE = 15;

// ── Thunks ──

/**
 * Fetch lightweight status counts (no profile lookups).
 * Used for the stat strip — always reflects total across all data.
 */
function fetchCounts(filters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("counts", true));
      const data = await fulfillmentApis.getFulfillmentCounts(filters);
      dispatch({ type: FULFILLMENT_SET_COUNTS, payload: data });
    } catch (error) {
      dispatch(setError(error.message));
    } finally {
      dispatch(setLoading("counts", false));
    }
  };
}

/* ── Pagination continuity ────────────────────────────────────────────────
   Two structural rules:

   1. An append continues the query page 1 recorded. Every page-1 fetch stores
      the filters it used on the slice; an append reuses those and IGNORES what
      the caller passed — otherwise a "load more" that changes the query
      splices a differently-sorted page in at the same offset, repeating rows
      already on screen and skipping the ones that should have come next.

   2. A list whose membership changed re-reads its window instead of skipping
      past it. Offsets are positions in a server-side set, and a mutation moves
      that set under us: complete a card and everything below it shifts up one,
      so the next `$skip: page*limit` jumps clean over a card no page will
      return again. `dirty` marks the drift; a dirty list re-reads pages 1..N
      in one request and replaces.

   Rule 2's mechanics (`dirty`, the bounded window re-read) live in
   `primitives.nextChunk`, shared with the calendar's own local lists. */

/** The query a paged slice is currently showing — an append must reuse it. */
function continuedFilters(slice, filters, append) {
  return append && slice?.filters ? slice.filters : filters;
}

/**
 * Fetch paginated data for a single board column.
 * @param {string} status - board COLUMN: fallback|assigned|in_progress|on_hold|completed
 * @param {Object} filters - { serviceSlug?, providerId? } — used on page 1
 *   only; an append reuses the filters page 1 recorded (see above).
 * @param {boolean} append - true = load more, false = replace (page 1)
 * @param {boolean} isAdmin - when true uses all-tasks, otherwise my-tasks
 */
function fetchColumnData(status, filters = {}, append = false, isAdmin = true) {
  return async (dispatch, getState) => {
    const key = `column_${status}`;
    dispatch(setLoading(key, true));
    try {
      const column = getState().fulfillment.columns[status];
      const used = continuedFilters(column, filters, append);
      const chunk = nextChunk(column, append, COLUMN_PAGE_SIZE);
      const data = await fulfillmentApis.getAllTasks({
        ...used,
        status,
        page: chunk.page,
        limit: chunk.limit,
      }, isAdmin);
      dispatch({
        type: FULFILLMENT_SET_COLUMN_DATA,
        payload: {
          status,
          // The slice's page pointer tracks how many pages the user has
          // asked for, not which page the request named.
          data: { ...data, page: chunk.resultPage, limit: COLUMN_PAGE_SIZE },
          append: chunk.append,
          filters: used,
        },
      });
    } catch (error) {
      dispatch(setError(error.message));
    } finally {
      dispatch(setLoading(key, false));
    }
  };
}

/**
 * Fetch all board data.
 *
 * Delivery admins: ONE $facet round-trip (`getBoard`) returning counts,
 * urgency rollups and page 1 of every column.
 *
 * Non-admin staff (moderators, providers): counts scoped server-side + the 4
 * visible columns paged through my-tasks.
 */
function fetchBoard(filters = {}, isOversight = true) {
  return async (dispatch) => {
    if (isOversight) {
      dispatch(setLoading("counts", true));
      try {
        const board = await fulfillmentApis.getBoard({
          ...filters,
          limit: COLUMN_PAGE_SIZE,
        });
        // The columns this fills are paged by a DIFFERENT endpoint
        // (`all-tasks`), so the query has to travel with them or continuity
        // falls back to whatever the load-more call site happens to pass.
        dispatch({
          type: FULFILLMENT_SET_BOARD,
          payload: { ...board, filters },
        });
      } catch (error) {
        dispatch(setError(errMsg(error, "Failed to load the board")));
      } finally {
        dispatch(setLoading("counts", false));
      }
      return;
    }
    dispatch({ type: FULFILLMENT_RESET_COLUMNS });
    dispatch(fetchCounts(filters));
    ["assigned", "in_progress", "on_hold", "completed"].forEach((status) => {
      dispatch(fetchColumnData(status, filters, false, isOversight));
    });
  };
}

/**
 * Full-board reconcile (Refresh button, task creation). Per-mutation updates
 * go through `refreshTask` instead — one card, patched in place.
 */
function refreshBoard(filters = {}, affectedStatuses, isOversight) {
  return (dispatch, getState) => {
    const role = getState().user?.profile?.role;
    // Oversight = ADMIN / MODERATOR / SERVICE_MANAGER (mirrors the server
    // DELIVERY_MANAGE_ROLES); everyone else is a self-scoped provider.
    const oversight =
      isOversight !== undefined
        ? isOversight
        : role === "ADMIN" ||
          role === "MODERATOR" ||
          role === "SERVICE_MANAGER";
    if (oversight) {
      dispatch(fetchBoard(filters, true));
      return;
    }
    dispatch(fetchCounts(filters));
    const allStatuses = ["assigned", "in_progress", "on_hold", "completed"];
    const statuses = affectedStatuses
      ? affectedStatuses.filter((s) => allStatuses.includes(s))
      : allStatuses;
    statuses.forEach((status) => {
      dispatch(fetchColumnData(status, filters, false, oversight));
    });
  };
}

/**
 * Patch ONE card in place after a mutation: re-fetch just that card and
 * move/update it across the loaded columns locally (status counts adjusted by
 * delta). If the card is no longer visible to us (cancelled, or access
 * changed) it is simply dropped from the board.
 */
function refreshTask(fulfillmentId) {
  return async (dispatch) => {
    if (!fulfillmentId) return;
    try {
      const item = await fulfillmentApis.getTaskById(fulfillmentId);
      if (item?.fulfillment?.fulfillmentId) {
        dispatch({ type: FULFILLMENT_PATCH_ITEM, payload: { item } });
      } else {
        dispatch({ type: FULFILLMENT_REMOVE_ITEM, payload: { fulfillmentId } });
      }
    } catch (err) {
      // A DEFINITIVE not-found (card cancelled/deleted, or access revoked)
      // removes it; a TRANSIENT error (5xx / network) must NOT vanish a card
      // that still exists — leave the board and let the next refresh retry. The
      // detail modal now depends on this refetch to hydrate its fat fields
      // (the list projection is lean), so a blip must degrade to "stale", never
      // to "gone". (404 shape mirrors the apiCall wrapper's own check.)
      if (err?.status === 404 || err?.response?.status === 404) {
        dispatch({ type: FULFILLMENT_REMOVE_ITEM, payload: { fulfillmentId } });
      }
    }
  };
}

/**
 * Users lens rows — paginated BY USER (the honest unit for that view).
 * append=true continues the list ("Load more users").
 */
function fetchUsers(filters = {}, append = false) {
  return async (dispatch, getState) => {
    dispatch(setLoading("users", true));
    try {
      const current = getState().fulfillment?.users || { page: 0 };
      // Roster rows are never patched by a card mutation (nothing writes into
      // `users`), so this slice needs continuity but not the dirty re-read.
      const used = continuedFilters(current, filters, append);
      const page = append ? (current.page || 1) + 1 : 1;
      const data = await fulfillmentApis.getUsersOverview({
        ...used,
        page,
        limit: USER_PAGE_SIZE,
      });
      dispatch({
        type: FULFILLMENT_SET_USERS,
        payload: { data, append, filters: used },
      });
    } catch (error) {
      const msg = errMsg(error, "Failed to load users");
      dispatch(setError(msg));
      toast.error(msg);
    } finally {
      dispatch(setLoading("users", false));
    }
  };
}

/**
 * One user-sectioned board column, paginated BY USER ("Load More users").
 * append=true continues the user list for that status.
 */
function fetchBoardUsers(status, filters = {}, append = false) {
  return async (dispatch, getState) => {
    const key = `busers_${status}`;
    dispatch(setLoading(key, true));
    try {
      const current = getState().fulfillment?.boardUsers?.[status] || { page: 0 };
      const used = continuedFilters(current, filters, append);
      // Sections are created and dropped by card patches, so the USER set
      // drifts exactly like a task column's — same dirty re-read.
      const chunk = nextChunk(current, append, USER_PAGE_SIZE);
      // No taskLimit: each section embeds ALL of the user's tasks in this
      // status (progressive disclosure keeps the DOM small; 15-user pages
      // bound the payload). There is no inner task pagination.
      const data = await fulfillmentApis.getBoardUsers({
        ...used,
        status,
        page: chunk.page,
        limit: chunk.limit,
      });
      dispatch({
        type: FULFILLMENT_SET_BOARD_USERS,
        payload: {
          status,
          data: { ...data, page: chunk.resultPage },
          append: chunk.append,
          filters: used,
        },
      });
    } catch (error) {
      const msg = errMsg(error, "Failed to load the board");
      dispatch(setError(msg));
      toast.error(msg);
    } finally {
      dispatch(setLoading(key, false));
    }
  };
}

// ── myTasks ──

function fetchMyTasks(params = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("myTasks", true));
      dispatch(setError(null));
      const data = await fulfillmentApis.getMyTasks(params);
      dispatch({ type: FULFILLMENT_SET_MY_TASKS, payload: data });
      return data;
    } catch (error) {
      dispatch(setError(error.message));
      return null;
    } finally {
      dispatch(setLoading("myTasks", false));
    }
  };
}

/**
 * The three Team reads share one shape and one rule: a FAILURE MUST BE VISIBLE.
 * A swallowed error leaves the list `[]`, indistinguishable from "nobody here
 * yet" — so an outage renders as "Add Provider…", an instruction. It also has a
 * live consequence: the Add-provider search hides managers (an ADMIN-only
 * list), so for a non-ADMIN oversight actor `fetchManagers` 403s silently and
 * the exclusion quietly stops happening.
 *
 * `setTeamError` records WHY a dataset is empty; the toast makes it audible.
 */
function setTeamError(key, message) {
  return { type: FULFILLMENT_SET_TEAM_ERROR, payload: { key, message } };
}

function fetchTeamData(key, run, action, fallbackMessage) {
  return async (dispatch) => {
    try {
      dispatch(setLoading(key, true));
      const data = await run();
      dispatch({ type: action, payload: data });
      dispatch(setTeamError(key, null));
      return data;
    } catch (error) {
      const msg = errMsg(error, fallbackMessage);
      dispatch(setError(msg));
      dispatch(setTeamError(key, msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading(key, false));
    }
  };
}

function fetchProviders(params = {}) {
  return fetchTeamData(
    "providers",
    () => fulfillmentApis.getProviders(params),
    FULFILLMENT_SET_PROVIDERS,
    "Failed to load the provider roster"
  );
}

function fetchManagers() {
  return fetchTeamData(
    "managers",
    () => fulfillmentApis.getManagers(),
    FULFILLMENT_SET_MANAGERS,
    "Failed to load managers"
  );
}

function fetchPendingRequests() {
  return fetchTeamData(
    "pendingRequests",
    () => fulfillmentApis.getPendingProviderRequests(),
    FULFILLMENT_SET_PENDING_REQUESTS,
    "Failed to load pending requests"
  );
}

// ── Mutation thunks ──

function assignProvider(data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("assign", true));
      const result = await fulfillmentApis.assignProvider(data);
      dispatch(refreshTask(data.fulfillmentId));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to assign provider");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("assign", false));
    }
  };
}

function advanceStage(fulfillmentId, data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("advanceStage", true));
      const result = await fulfillmentApis.advanceStage(fulfillmentId, data);
      dispatch(refreshTask(fulfillmentId));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to advance stage");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("advanceStage", false));
    }
  };
}

function skipFulfillmentAction(fulfillmentId, data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("skip", true));
      const result = await fulfillmentApis.skipFulfillment(fulfillmentId, data);
      // Cancelled cards leave the board — drop locally, no refetch.
      dispatch({ type: FULFILLMENT_REMOVE_ITEM, payload: { fulfillmentId } });
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to cancel the task");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("skip", false));
    }
  };
}

/**
 * Put an out-of-circulation at_claim credit back in the claimable pool.
 *
 * The inverse of `skipFulfillmentAction`, and it refetches rather than patching
 * locally for a reason the cancel path does not have: the thing that changed is
 * a NEW card (a fresh unclaimed stub) which is not in the store at all, so
 * there is nothing to patch. `FULFILLMENT_REMOVE_ITEM`'s local-drop trick works
 * for cancel precisely because the card leaves our view; here one arrives.
 */
function returnCreditToPoolAction(data, activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("returnCredit", true));
      const result = await fulfillmentApis.returnCreditToPool(data);
      toast.success("Credit returned to the pool");
      // The new stub is not in the store, so refetch the columns it can land in:
      // minted ownerless (fallback), then push-assigned to an owner (assigned).
      // `refreshBoard` (not a bare fetch) so a self-scoped provider gets their
      // own narrower refresh instead of an oversight query they can't make.
      dispatch(refreshBoard(activeFilters, ["fallback", "assigned"]));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to return the credit");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("returnCredit", false));
    }
  };
}

function completeFulfillment(fulfillmentId, data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("complete", true));
      const result = await fulfillmentApis.completeFulfillment(fulfillmentId, data);
      dispatch(refreshTask(fulfillmentId));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to complete the task");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("complete", false));
    }
  };
}

function updateStage(fulfillmentId, stageKey, data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("updateStage", true));
      const result = await fulfillmentApis.updateStage(fulfillmentId, stageKey, data);
      dispatch(refreshTask(fulfillmentId));
      return result;
    } catch (error) {
      // No toast here — the detail modal renders this error inline next to
      // the stage that failed (the only mutation with a local error surface).
      const msg = errMsg(error, "Failed to update the stage");
      dispatch(setError(msg));
      return { error: msg };
    } finally {
      dispatch(setLoading("updateStage", false));
    }
  };
}

function reassignStage(fulfillmentId, stageKey, data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("reassignStage", true));
      const result = await fulfillmentApis.reassignStage(
        fulfillmentId,
        stageKey,
        data
      );
      dispatch(refreshTask(fulfillmentId));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to reassign the stage");
      dispatch(setError(msg));
      toast.error(msg);
      throw new Error(msg);
    } finally {
      dispatch(setLoading("reassignStage", false));
    }
  };
}

function updateFulfillmentNotes(fulfillmentId, data) {
  return async (dispatch) => {
    try {
      return await fulfillmentApis.updateFulfillmentNotes(fulfillmentId, data);
    } catch (error) {
      const msg = errMsg(error, "Failed to save notes");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    }
  };
}

/**
 * Save ONE stage's internal note. Pass an empty `notes` to clear it.
 *
 * Refreshes the task afterwards, unlike the card-level notes action above: the
 * author name is stamped server-side, so re-reading the card is how the byline
 * appears without the client guessing at it.
 *
 * Toasts AND returns `{ error }`. The editor renders the error inline, but a
 * dirty editor also flushes when the inspector unmounts — and at that point
 * there is no inline surface left to render into, so the toast is the only way
 * a failed save is ever seen.
 */
function updateStageNotes(fulfillmentId, stageKey, data) {
  return async (dispatch) => {
    try {
      const result = await fulfillmentApis.updateStageNotes(
        fulfillmentId,
        stageKey,
        data
      );
      dispatch(refreshTask(fulfillmentId));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to save the stage note");
      dispatch(setError(msg));
      toast.error(msg);
      return { error: msg };
    }
  };
}

function keepFallbackCard(fulfillmentId, data) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("keep", true));
      const result = await fulfillmentApis.keepFallbackCard(fulfillmentId, data);
      // The card leaves the Needs-expert column on this write, so the board has
      // to be told: refreshTask re-fetches it and the reducer re-buckets it by
      // the server's `_column`.
      dispatch(refreshTask(fulfillmentId));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Couldn't keep this card");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("keep", false));
    }
  };
}

function reassignProviderAction(fulfillmentId, data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("reassign", true));
      const result = await fulfillmentApis.reassignProvider(fulfillmentId, data);
      dispatch(refreshTask(fulfillmentId));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to reassign provider");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("reassign", false));
    }
  };
}

function updateProviderConfig(providerId, data) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("providerConfig", true));
      const result = await fulfillmentApis.updateProviderConfig(providerId, data);
      dispatch(fetchProviders());
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to update provider settings");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("providerConfig", false));
    }
  };
}

function removeProvider(providerId) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("providerConfig", true));
      const result = await fulfillmentApis.removeProvider(providerId);
      dispatch(fetchProviders());
      // Removal clears providerConfig, and the roster is what the Add-manager
      // search reads to build its exclusions (provider and manager are disjoint
      // populations) — so the sibling list is stale the moment this lands.
      dispatch(fetchManagers());
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to remove the provider");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("providerConfig", false));
    }
  };
}

function updateManagerConfig(gamePlayerId, data) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("managerConfig", true));
      const result = await fulfillmentApis.updateManagerConfig(
        gamePlayerId,
        data
      );
      dispatch(fetchManagers());
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to update manager settings");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("managerConfig", false));
    }
  };
}

function setManagerStatus(gamePlayerId, isActive) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("managerConfig", true));
      const result = await fulfillmentApis.setManagerStatus(
        gamePlayerId,
        isActive
      );
      dispatch(fetchManagers());
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to update manager status");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("managerConfig", false));
    }
  };
}

function decideProviderRequest(gamePlayerId, decision, decisionNote) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("providerConfig", true));
      const result = await fulfillmentApis.decideProviderRequest(
        gamePlayerId,
        decision,
        decisionNote
      );
      dispatch(fetchPendingRequests());
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to record the decision");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("providerConfig", false));
    }
  };
}

function demoteManager(gamePlayerId) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("managerConfig", true));
      const result = await fulfillmentApis.demoteManager(gamePlayerId);
      dispatch(fetchManagers());
      // They are eligible for the provider roster again, so the sibling list
      // (which the Add-provider search reads to build its exclusions) is stale.
      dispatch(fetchProviders());
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to remove the manager");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("managerConfig", false));
    }
  };
}

function promoteManager(data) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("managerConfig", true));
      const result = await fulfillmentApis.promoteManager(data);
      dispatch(fetchManagers());
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to add manager");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("managerConfig", false));
    }
  };
}

function holdFulfillmentAction(fulfillmentId, data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("hold", true));
      const result = await fulfillmentApis.holdFulfillment(fulfillmentId, data);
      dispatch(refreshTask(fulfillmentId));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to put the task on hold");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("hold", false));
    }
  };
}

function resumeFulfillmentAction(fulfillmentId, data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("resume", true));
      const result = await fulfillmentApis.resumeFulfillment(fulfillmentId, data);
      dispatch(refreshTask(fulfillmentId));
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to resume the task");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("resume", false));
    }
  };
}

function startServiceDeliveryAction(fulfillmentId, data, _activeFilters = {}) {
  return async (dispatch) => {
    try {
      dispatch(setLoading("startDelivery", true));
      const result = await fulfillmentApis.startServiceDelivery(
        fulfillmentId,
        data
      );
      dispatch(refreshTask(fulfillmentId));
      toast.success("Delivery started — the SLA clock is now running.");
      return result;
    } catch (error) {
      const msg = errMsg(error, "Failed to start service delivery");
      dispatch(setError(msg));
      toast.error(msg);
      return null;
    } finally {
      dispatch(setLoading("startDelivery", false));
    }
  };
}

// ── Assignment policy thunks ──

/* The policy thunks toast on failure AND return `{ ok, error }` so a caller can
   branch on it: a toast is the right ambient signal, but the save path needs to
   KEEP THE MODAL OPEN, which no toast can do. (`fulfillmentSelectors.getError`
   is read by no component, so the redux error channel alone is invisible.) */
function policyFailure(dispatch, error, fallback, { toastIt = true } = {}) {
  const msg = errMsg(error, fallback);
  dispatch(setError(msg));
  if (toastIt) toast.error(msg);
  // Field-level issues from validateAssignmentPolicy, so the builder can point
  // at the control that caused it rather than only naming it in prose; `code`
  // separates a stale-write 409 (which needs a reload, not an edit) from an
  // invalid policy (which needs an edit, not a reload).
  return {
    ok: false,
    error: msg,
    issues: error?.response?.data?.issues || [],
    code: error?.response?.data?.code || null,
  };
}

function fetchAssignmentPolicies() {
  return async (dispatch) => {
    try {
      dispatch(setLoading("assignmentPolicies", true));
      const data = await fulfillmentApis.listAssignmentPolicies();
      dispatch({ type: FULFILLMENT_SET_ASSIGNMENT_POLICIES, payload: data });
      return data;
    } catch (error) {
      policyFailure(dispatch, error, "Failed to load assignment rules");
      return null;
    } finally {
      dispatch(setLoading("assignmentPolicies", false));
    }
  };
}

function fetchAssignmentPolicy(slug) {
  return async (dispatch) => {
    try {
      dispatch(setLoading(`assignmentPolicy_${slug}`, true));
      const data = await fulfillmentApis.getAssignmentPolicy(slug);
      dispatch({
        type: FULFILLMENT_SET_ASSIGNMENT_POLICY_DETAIL,
        payload: { slug, data },
      });
      return data;
    } catch (error) {
      policyFailure(dispatch, error, "Failed to load this policy");
      return null;
    } finally {
      dispatch(setLoading(`assignmentPolicy_${slug}`, false));
    }
  };
}

/** Resolves `{ ok: true, data }` or `{ ok: false, error, issues }`. */
function saveAssignmentPolicy(slug, policy) {
  return async (dispatch) => {
    try {
      dispatch(setLoading(`assignmentPolicy_${slug}`, true));
      const data = await fulfillmentApis.updateAssignmentPolicy(slug, policy);
      await dispatch(fetchAssignmentPolicies());
      await dispatch(fetchAssignmentPolicy(slug));
      return { ok: true, data };
    } catch (error) {
      // No toast: the builder renders this inline, next to the offending
      // control. A toast as well would be the same news twice.
      return policyFailure(dispatch, error, "Failed to save the policy", {
        toastIt: false,
      });
    } finally {
      dispatch(setLoading(`assignmentPolicy_${slug}`, false));
    }
  };
}

function dryRunAssignmentPolicy(slug, policy, buyerFacts) {
  return async (dispatch) => {
    try {
      return {
        ok: true,
        data: await fulfillmentApis.dryRunAssignmentPolicy(
          slug,
          policy,
          buyerFacts
        ),
      };
    } catch (error) {
      return policyFailure(dispatch, error, "Simulation failed");
    }
  };
}

function retryFallbackForService(slug) {
  return async (dispatch) => {
    try {
      dispatch(setLoading(`retryFallback_${slug}`, true));
      const data = await fulfillmentApis.retryFallbackForService(slug);
      const { assigned = 0, attempted = 0, truncated } = data || {};
      if (attempted === 0) {
        toast.info("No cards are sitting on the fallback for this service.");
      } else {
        toast.success(
          `Matched ${assigned} of ${attempted} card(s) to a provider.${
            truncated ? " More remain — run it again." : ""
          }`
        );
      }
      return { ok: true, data };
    } catch (error) {
      return policyFailure(dispatch, error, "Retry failed");
    } finally {
      dispatch(setLoading(`retryFallback_${slug}`, false));
    }
  };
}

// ── Exported actions ──

export const fulfillmentActions = {
  setActiveTab,
  fetchCounts,
  fetchColumnData,
  fetchBoard,
  refreshBoard,
  refreshTask,
  fetchUsers,
  fetchBoardUsers,
  fetchMyTasks,
  fetchProviders,
  fetchManagers,
  fetchPendingRequests,
  assignProvider,
  advanceStage,
  completeFulfillment,
  skipFulfillment: skipFulfillmentAction,
  returnCreditToPool: returnCreditToPoolAction,
  updateStage,
  updateStageNotes,
  reassignStage,
  updateFulfillmentNotes,
  keepFallbackCard,
  reassignProvider: reassignProviderAction,
  holdFulfillment: holdFulfillmentAction,
  resumeFulfillment: resumeFulfillmentAction,
  startServiceDelivery: startServiceDeliveryAction,
  updateProviderConfig,
  removeProvider,
  decideProviderRequest,
  updateManagerConfig,
  promoteManager,
  setManagerStatus,
  demoteManager,
  fetchAssignmentPolicies,
  fetchAssignmentPolicy,
  saveAssignmentPolicy,
  dryRunAssignmentPolicy,
  retryFallbackForService,
};

// ── Selectors ──

const DEFAULT_COLUMN = { items: [], total: 0, page: 1, limit: COLUMN_PAGE_SIZE, hasMore: false };
const NO_MUTATION = { seq: 0, fulfillmentId: null, item: null };

export const fulfillmentSelectors = {
  getCounts: (state) => state.fulfillment?.counts || {},
  // Triage rollups (admin board): overdue / dueToday / waitingOnCustomer.
  getUrgency: (state) => state.fulfillment?.urgency || { overdue: 0, dueToday: 0, waitingOnCustomer: 0 },
  // True when the board's numbers describe a clipped search cohort.
  isSearchTruncated: (state) => state.fulfillment?.searchTruncated === true,
  getColumn: (status) => (state) => state.fulfillment?.columns?.[status] || DEFAULT_COLUMN,
  getColumns: (state) => state.fulfillment?.columns || {},
  getUsers: (state) =>
    state.fulfillment?.users || { items: [], total: 0, page: 1, hasMore: false },
  getBoardUsers: (state) => state.fulfillment?.boardUsers || {},
  getMyTasks: (state) => state.fulfillment?.myTasks,
  getProviders: (state) => state.fulfillment?.providers,
  getManagers: (state) => state.fulfillment?.managers,
  getPendingRequests: (state) => state.fulfillment?.pendingRequests,
  // Why a Team list is empty: null = it loaded fine, a string = it failed.
  getTeamError: (key) => (state) => state.fulfillment?.teamErrors?.[key] || null,
  isLoading: (key) => (state) => state.fulfillment?.loading?.[key] || false,
  // True while any board data (counts or a column page) is in flight — feeds
  // the Refresh button's spinner so it reflects real work, not a timer.
  isRefreshing: (state) => {
    const loading = state.fulfillment?.loading || {};
    return !!(
      loading.counts ||
      loading.users ||
      Object.keys(loading).some((k) => k.startsWith("column_") && loading[k])
    );
  },
  // The last single-card mutation ({ seq, fulfillmentId, item|null }). Any
  // surface holding its own rows subscribes to this instead of going stale —
  // see `cardChanged`. The fallback is a shared constant, not a literal:
  // `useSelector` compares by reference, so returning a fresh object would
  // re-render every subscriber on every dispatch in the app.
  getLastMutation: (state) => state.fulfillment?.lastMutation || NO_MUTATION,
  getError: (state) => state.fulfillment?.error,
  getActiveTab: (state) => state.fulfillment?.activeTab || "tasks",
  getAssignmentPolicies: (state) => state.fulfillment?.assignmentPolicies || [],
  getAssignmentPolicyDetail: (slug) => (state) =>
    state.fulfillment?.assignmentPolicyDetails?.[slug] || null,
  // Helper: get all loaded items across all columns (for detail modal lookup)
  getAllLoadedItems: (state) => {
    const cols = state.fulfillment?.columns || {};
    const boardUsers = state.fulfillment?.boardUsers || {};
    return [
      ...(cols.fallback?.items || []),
      ...(cols.assigned?.items || []),
      ...(cols.in_progress?.items || []),
      ...(cols.on_hold?.items || []),
      ...(cols.completed?.items || []),
      // Tasks embedded in user-sectioned board columns count too — the
      // detail modal's liveItem lookup and patch overlays read this.
      ...Object.values(boardUsers).flatMap((col) =>
        (col?.items || []).flatMap((u) => u.tasks || [])
      ),
    ];
  },
  // The last card refreshed by id (FAT). The detail modal prefers this over the
  // loaded-columns lookup so a card in no loaded column (e.g. a cancelled card
  // opened from the archive) still shows its rich, modal-only fields.
  getDetailCard: (state) => state.fulfillment?.detailCard,
};

// ── Reducer ──

const initialColumnState = { items: [], total: 0, page: 1, limit: COLUMN_PAGE_SIZE, hasMore: false };

const initialState = {
  counts: { fallback: 0, assigned: 0, in_progress: 0, on_hold: 0, completed: 0 },
  urgency: { overdue: 0, dueToday: 0, waitingOnCustomer: 0 },
  // Set by the last board read: the customer search matched more customers
  // than the server's candidate cap admits, so the board is describing a
  // subset. Never inferred client-side — only the resolver can know.
  searchTruncated: false,
  columns: {
    fallback: { ...initialColumnState },
    assigned: { ...initialColumnState },
    in_progress: { ...initialColumnState },
    on_hold: { ...initialColumnState },
    completed: { ...initialColumnState },
  },
  users: { items: [], total: 0, page: 1, limit: 15, hasMore: false },
  // User-sectioned board columns: { [status]: { items: [userSection],
  // totalUsers, page, hasMore } } — userSection = identity + taskTotal +
  // tasks[] (first pages) + tasksPage.
  boardUsers: {},
  myTasks: { items: [], total: 0, page: 1, limit: 20 },
  providers: [],
  managers: [],
  pendingRequests: [],
  // Per-dataset load failure for the Team tab, keyed like `loading`. Lets an
  // empty list distinguish "it failed" from "nobody here yet" (see setTeamError).
  teamErrors: {},
  assignmentPolicies: [],
  assignmentPolicyDetails: {},
  loading: {},
  error: null,
  activeTab: "tasks",
  // Last single-card mutation, for surfaces that don't live in `columns`.
  // seq 0 = nothing has happened yet, so a subscriber can tell "mount" from
  // "a card just changed" and not react to its own first render.
  lastMutation: { seq: 0, fulfillmentId: null, item: null },
  // Last card refreshed by id (FAT) — the detail modal's store-first source for
  // its heavy, modal-only fields, which the lean list projection omits.
  detailCard: null,
};

/**
 * The "a card changed" announcement, for surfaces that keep their own rows
 * outside `columns` / `boardUsers` / `counts` (e.g. the calendar's local
 * `useState` datasets) and would otherwise never learn a mutation happened.
 * Published on the same seam as the patch/remove, not via a second mechanism.
 *
 * `item: null` means the card left our view (cancelled, or access changed).
 * `seq` is what subscribers watch — two mutations of the SAME card must be two
 * events, which an identity check on the payload alone would miss.
 */
function cardChanged(state, fulfillmentId, item) {
  return {
    seq: (state.lastMutation?.seq || 0) + 1,
    fulfillmentId: fulfillmentId ? fulfillmentId.toString() : null,
    item: item || null,
  };
}

/** One row per customer — the unit both user-paginated lenses are keyed by. */
function dedupeByGamePlayerId(rows) {
  const seen = new Set();
  return (rows || []).filter((u) => {
    const id = String(u?.gamePlayerId ?? "");
    if (!id || seen.has(id)) return !id;
    seen.add(id);
    return true;
  });
}

/** Locate which loaded column currently holds a card. */
function findColumnOf(columns, fulfillmentId) {
  for (const key of Object.keys(columns)) {
    const idx = (columns[key]?.items || []).findIndex(
      (i) => i?.fulfillment?.fulfillmentId === fulfillmentId
    );
    if (idx >= 0) return { key, idx };
  }
  return null;
}

const withHasMore = (col) => ({
  ...col,
  hasMore: (col.items || []).length < (col.total || 0),
});

/* A paged slice whose MEMBERSHIP changed has drifted away from the offsets it
   was paged with — the server's set shifted under our page cursor. Mark it so
   the next "load more" re-reads the window instead of skipping past a card
   that slid up into it. An in-place replacement never sets this: the row
   changed, the set didn't. */
const markDirty = (col) => (col ? { ...col, dirty: true } : col);

/* Sectioned columns page by USER, so they drift when a section is CREATED or
   DROPPED — not when a task moves inside one. Comparing section counts is the
   whole test, and it is why this is applied to the result of the two patch
   helpers rather than inside them. */
function markBoardUsersDrift(prev, next) {
  if (prev === next) return next;
  const out = {};
  for (const [status, col] of Object.entries(next || {})) {
    const before = (prev?.[status]?.items || []).length;
    const after = (col?.items || []).length;
    out[status] = before === after ? col : markDirty(col);
  }
  return out;
}

/* ── boardUsers (user-sectioned columns) patch helpers ──
   Keep the sectioned columns in sync with single-card patches: remove the
   card from whichever section holds it; insert into the destination status's
   section (creating a section at the top if the user isn't loaded there —
   without count math, mirroring the columns' no-double-count rule). */
/**
 * Patch ONE task inside the user-sectioned columns.
 *
 * The naive shape — remove it, then insert it — is correct about membership and
 * wrong about ORDER: `boardUsersInsertTask` puts a task at the head of its
 * user's list, and re-creates an emptied section at the head of the column. So
 * merely REFRESHING a card (which is what opening one does) jumped it, and
 * sometimes its whole section, to the top. The board reordered under the
 * operator with nothing to explain it.
 *
 * A patch that does not move the task between columns is an in-place
 * replacement, exactly as the flat columns already treat it. Only a real move
 * falls through to remove-then-insert, where landing at the head is right:
 * the card genuinely just arrived.
 */
function boardUsersPatchTask(boardUsers, item) {
  const id = item?.fulfillment?.fulfillmentId;
  // The COLUMN, not the status. A fallback card is `assigned` on the wire but
  // lives in `fallback`, so comparing the raw status against where it actually
  // sits would read every in-place patch as a move — reordering it AND
  // dropping it into the wrong section.
  const dest = columnOf(item);
  if (!id || !boardUsers) return boardUsers;

  // Where does it sit now? Collected across every column, so a task that is
  // somehow in two places falls through to the remove-then-insert path rather
  // than being half-fixed here.
  const found = [];
  for (const [status, col] of Object.entries(boardUsers)) {
    (col?.items || []).forEach((u, userIdx) => {
      const taskIdx = (u?.tasks || []).findIndex(
        (t) => t?.fulfillment?.fulfillmentId === id
      );
      if (taskIdx >= 0) found.push({ status, userIdx, taskIdx });
    });
  }

  const here = found.length === 1 ? found[0] : null;
  const sameUser =
    here &&
    String(boardUsers[here.status].items[here.userIdx]?.gamePlayerId) ===
      String(item.gamePlayerId);

  // In place only when nothing about WHERE it belongs has moved — same column,
  // same section. Anything else is a real relocation and takes the path below.
  if (here && here.status === dest && sameUser) {
    const { status, userIdx, taskIdx } = here;
    const col = boardUsers[status];
    const users = [...col.items];
    const tasks = [...(users[userIdx].tasks || [])];
    tasks[taskIdx] = item;
    users[userIdx] = { ...users[userIdx], tasks };
    return { ...boardUsers, [status]: { ...col, items: users } };
  }

  return boardUsersInsertTask(boardUsersRemoveTask(boardUsers, id), item);
}

function boardUsersRemoveTask(boardUsers, fulfillmentId) {
  let changed = false;
  const next = {};
  for (const [status, col] of Object.entries(boardUsers || {})) {
    const items = (col.items || []).map((u) => {
      const had = (u.tasks || []).some(
        (t) => t?.fulfillment?.fulfillmentId === fulfillmentId
      );
      if (!had) return u;
      changed = true;
      return {
        ...u,
        tasks: u.tasks.filter(
          (t) => t?.fulfillment?.fulfillmentId !== fulfillmentId
        ),
        taskTotal: Math.max(0, (u.taskTotal || 0) - 1),
      };
    });
    // Drop sections that emptied out entirely.
    next[status] = {
      ...col,
      items: items.filter((u) => (u.taskTotal || 0) > 0 || (u.tasks || []).length > 0),
    };
  }
  return changed ? next : boardUsers;
}

function boardUsersInsertTask(boardUsers, item) {
  // Same partition as the flat columns — see columnOf.
  const status = columnOf(item);
  const col = boardUsers?.[status];
  if (!col) return boardUsers; // destination column not loaded — nothing to do
  const gpId = String(item.gamePlayerId);
  const idx = (col.items || []).findIndex(
    (u) => String(u.gamePlayerId) === gpId
  );
  const items = [...(col.items || [])];
  if (idx >= 0) {
    items[idx] = {
      ...items[idx],
      tasks: [item, ...(items[idx].tasks || [])],
      taskTotal: (items[idx].taskTotal || 0) + 1,
    };
  } else {
    items.unshift({
      gamePlayerId: item.gamePlayerId,
      studentName: item.studentName,
      studentUserName: item.studentUserName,
      studentAvatar: item.studentAvatar,
      planName: item.planName,
      overdue: 0,
      taskTotal: 1,
      tasks: [item],
      tasksPage: 1,
    });
  }
  return { ...boardUsers, [status]: { ...col, items } };
}

export const fulfillmentReducer = createReducer(initialState, {
  // Whole board from the single $facet endpoint.
  [FULFILLMENT_SET_BOARD]: (state, payload) => {
    const cols = payload?.columns || {};
    const columns = {};
    for (const key of Object.keys(initialState.columns)) {
      const c = cols[key] || { items: [], total: 0 };
      columns[key] = withHasMore({
        items: c.items || [],
        total: c.total || 0,
        page: 1,
        limit: COLUMN_PAGE_SIZE,
        // A fresh full-board read: back in step with the server, and carrying
        // the query its "load more" must continue.
        dirty: false,
        filters: payload?.filters,
      });
    }
    return {
      ...state,
      columns,
      counts: { ...initialState.counts, ...(payload?.counts || {}) },
      urgency: { ...initialState.urgency, ...(payload?.urgency || {}) },
      // Did the customer-search cap clip the cohort every number above
      // describes? The board must be able to say so rather than present a
      // server-computed total over a silently narrowed set.
      searchTruncated: payload?.searchTruncated === true,
    };
  },
  [FULFILLMENT_SET_BOARD_USERS]: (state, payload) => {
    const { status, data, append, filters } = payload;
    const existing = state.boardUsers?.[status] || { items: [], page: 0 };
    const items = dedupeByGamePlayerId(
      append ? [...existing.items, ...(data?.items || [])] : data?.items || []
    );
    return {
      ...state,
      boardUsers: {
        ...state.boardUsers,
        [status]: {
          items,
          totalUsers: data?.totalUsers || 0,
          page: data?.page || 1,
          hasMore: items.length < (data?.totalUsers || 0),
          dirty: append ? !!existing.dirty : false,
          filters: filters ?? existing.filters,
        },
      },
    };
  },
  // One card patched in place: replace it, or move it between columns with
  // ±1 on the affected totals/counts. Urgency rollups drift until the next
  // board fetch (Refresh / filter change) — acceptable, they're rollups.
  [FULFILLMENT_PATCH_ITEM]: (state, payload) => {
    const item = payload.item;
    const id = item?.fulfillment?.fulfillmentId;
    if (!id) return state;
    // The COLUMN, not the status: a fallback card is `assigned` on the wire.
    const newStatus = columnOf(item);
    // Sectioned columns first: patched in place, or relocated if it moved.
    const boardUsers = markBoardUsersDrift(
      state.boardUsers,
      boardUsersPatchTask(state.boardUsers, item)
    );
    // `detailCard` is the last card refreshed by id (getTaskById → FAT). The
    // detail modal prefers it over the loaded-columns lookup, so its rich
    // fields survive even for a card that lives in NO loaded column — a
    // cancelled card opened from the archive, which `columnOf` maps to
    // `cancelled` (no such column) and so can never re-home below. Since the
    // board list items are now the lean projection, that store-first read is
    // the only place the modal's heavy fields come from.
    state = {
      ...state,
      boardUsers,
      detailCard: item,
      lastMutation: cardChanged(state, id, item),
    };
    const columns = { ...state.columns };
    const counts = { ...state.counts };
    const loc = findColumnOf(columns, id);
    if (loc && loc.key === newStatus) {
      const items = [...columns[loc.key].items];
      items[loc.idx] = item;
      columns[loc.key] = { ...columns[loc.key], items };
      return { ...state, columns };
    }
    if (loc) {
      const src = columns[loc.key];
      // Dirty: the card left this column's server-side set, so everything
      // below it shifted up and our page offsets no longer line up.
      columns[loc.key] = markDirty(withHasMore({
        ...src,
        items: src.items.filter((_, i) => i !== loc.idx),
        total: Math.max(0, (src.total || 0) - 1),
      }));
      counts[loc.key] = Math.max(0, (counts[loc.key] || 0) - 1);
    }
    if (Object.prototype.hasOwnProperty.call(columns, newStatus)) {
      const dest = columns[newStatus];
      // Count/total math only applies to a MOVE between loaded columns. A
      // card we didn't have loaded (deep link / beyond page 1) was already in
      // the server-side totals — inserting it must not double-count.
      // Dirty either way: we put a row at the head that the server would have
      // sorted somewhere else, so this window no longer matches an offset.
      columns[newStatus] = markDirty(withHasMore({
        ...dest,
        items: [item, ...(dest.items || [])],
        total: loc ? (dest.total || 0) + 1 : dest.total || 0,
      }));
      if (loc) counts[newStatus] = (counts[newStatus] || 0) + 1;
    }
    return { ...state, columns, counts };
  },
  // A card left our view (cancelled / access changed): drop it locally.
  [FULFILLMENT_REMOVE_ITEM]: (state, payload) => {
    const { fulfillmentId } = payload;
    state = {
      ...state,
      // Drop the cached FAT snapshot too, so it can't outlive the card. The
      // detail modal prefers `detailCard` by id — leaving a removed card's
      // snapshot here would repaint its stale PRE-removal state on re-open (a
      // just-cancelled card with no cancelled banner and an ENABLED action bar,
      // whose stage-advance is not status-guarded server-side) until the next
      // by-id refetch lands. `skipFulfillmentAction` removes without a refetch,
      // so this is the only thing that clears it in that path.
      detailCard:
        state.detailCard?.fulfillment?.fulfillmentId === fulfillmentId
          ? null
          : state.detailCard,
      boardUsers: markBoardUsersDrift(
        state.boardUsers,
        boardUsersRemoveTask(state.boardUsers, fulfillmentId)
      ),
      // Stamped BEFORE the `!loc` bail-out below. A card cancelled from a
      // surface that isn't the board is usually in no loaded column at all —
      // which is exactly the case the calendar needs to hear about.
      lastMutation: cardChanged(state, fulfillmentId, null),
    };
    const columns = { ...state.columns };
    const counts = { ...state.counts };
    const loc = findColumnOf(columns, fulfillmentId);
    if (!loc) return state;
    const src = columns[loc.key];
    columns[loc.key] = markDirty(withHasMore({
      ...src,
      items: src.items.filter(
        (i) => i?.fulfillment?.fulfillmentId !== fulfillmentId
      ),
      total: Math.max(0, (src.total || 0) - 1),
    }));
    counts[loc.key] = Math.max(0, (counts[loc.key] || 0) - 1);
    return { ...state, columns, counts };
  },
  [FULFILLMENT_SET_COUNTS]: (state, payload) => ({
    ...state,
    counts: {
      fallback: payload?.fallback || 0,
      assigned: payload?.assigned || 0,
      in_progress: payload?.in_progress || 0,
      on_hold: payload?.on_hold || 0,
      completed: payload?.completed || 0,
    },
  }),
  [FULFILLMENT_SET_COLUMN_DATA]: (state, payload) => {
    const { status, data, append, filters } = payload;
    const existing = state.columns[status] || initialColumnState;
    const newItems = append
      ? [...existing.items, ...(data?.items || [])]
      : (data?.items || []);
    return {
      ...state,
      columns: {
        ...state.columns,
        [status]: {
          items: newItems,
          total: data?.total || 0,
          page: data?.page || 1,
          limit: data?.limit || COLUMN_PAGE_SIZE,
          hasMore: newItems.length < (data?.total || 0),
          // Only a read that REPLACED the window re-synchronises it. An
          // append leaves any drift standing — a mutation that landed while
          // the request was in flight must not be forgotten by its response.
          dirty: append ? !!existing.dirty : false,
          // The query this list is now showing; an append must continue it.
          filters: filters ?? existing.filters,
        },
      },
    };
  },
  [FULFILLMENT_RESET_COLUMNS]: (state) => ({
    ...state,
    columns: {
      fallback: { ...initialColumnState },
      assigned: { ...initialColumnState },
      in_progress: { ...initialColumnState },
      on_hold: { ...initialColumnState },
      completed: { ...initialColumnState },
    },
  }),
  [FULFILLMENT_SET_USERS]: (state, payload) => {
    const { data, append, filters } = payload;
    const existing = state.users || initialState.users;
    // One row per customer. Continuity (above) stops the gross duplication a
    // re-sorted page 2 caused, but a user whose rank changes between two
    // requests can still land on both pages — and this list is keyed by
    // customer, so showing one twice is never right.
    const items = dedupeByGamePlayerId(
      append ? [...existing.items, ...(data?.items || [])] : data?.items || []
    );
    return {
      ...state,
      users: {
        items,
        total: data?.total || 0,
        page: data?.page || 1,
        limit: data?.limit || USER_PAGE_SIZE,
        hasMore: items.length < (data?.total || 0),
        filters: filters ?? existing.filters,
      },
    };
  },
  [FULFILLMENT_SET_MY_TASKS]: (state, payload) => ({
    ...state,
    myTasks: payload || initialState.myTasks,
  }),
  [FULFILLMENT_SET_PROVIDERS]: (state, payload) => ({
    ...state,
    providers: payload || [],
  }),
  [FULFILLMENT_SET_MANAGERS]: (state, payload) => ({
    ...state,
    managers: payload || [],
  }),
  [FULFILLMENT_SET_PENDING_REQUESTS]: (state, payload) => ({
    ...state,
    pendingRequests: payload || [],
  }),
  [FULFILLMENT_SET_TEAM_ERROR]: (state, payload) => ({
    ...state,
    teamErrors: { ...state.teamErrors, [payload.key]: payload.message },
  }),
  [FULFILLMENT_SET_LOADING]: (state, payload) => ({
    ...state,
    loading: { ...state.loading, [payload.key]: payload.value },
  }),
  [FULFILLMENT_SET_ERROR]: (state, payload) => ({
    ...state,
    error: payload,
  }),
  [FULFILLMENT_SET_ACTIVE_TAB]: (state, payload) => ({
    ...state,
    activeTab: payload,
  }),
  [FULFILLMENT_SET_ASSIGNMENT_POLICIES]: (state, payload) => ({
    ...state,
    assignmentPolicies: Array.isArray(payload) ? payload : [],
  }),
  [FULFILLMENT_SET_ASSIGNMENT_POLICY_DETAIL]: (state, payload) => ({
    ...state,
    assignmentPolicyDetails: {
      ...state.assignmentPolicyDetails,
      [payload.slug]: payload.data,
    },
  }),
});
